import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  PATIENT_STATUSES, ENCOUNTER_TYPES, VISIT_TYPES, 
  PANEL_OWNERSHIP, APPOINTMENT_TYPES, REASON_FOR_VISIT_OPTIONS 
} from '../../constants/mediscribe';

/**
 * ProviderDemographics - Demographics form for Provider mode
 * Extracted from App.js for maintainability
 */
export const ProviderDemographics = ({
  patientAge,
  setPatientAge,
  patientGender,
  setPatientGender,
  encounterType,
  setEncounterType,
  visitType,
  setVisitType,
  panelOwnership,
  setPanelOwnership,
  appointmentType,
  setAppointmentType,
  reasonForVisit,
  setReasonForVisit,
  customReasonForVisit,
  setCustomReasonForVisit,
  patientStatus,
  setPatientStatus,
  autoFilledFields,
  setAutoFilledFields,
  setIncludePE,
  setPeUnremarkable
}) => {
  return (
    <>
      {/* Mobile: 2-column grid, Desktop: flex row - Larger inputs
          Feb 27, 2026 — Age / Gender / Type fields removed from here; they
          live in the always-visible Quick Demographics tile above the notes
          textarea. This panel now hosts ONLY the visit-detail fields that
          aren't in the quick tile (Visit Type, Appointment Type, Status). */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3 items-end">
        {/* Visit Type */}
        <div className="sm:w-32">
          <Label className="text-[10px] sm:text-xs text-muted-foreground">Visit</Label>
          <Select value={visitType || '_none'} onValueChange={(val) => {
            const v = val === '_none' ? '' : val;
            setVisitType(v);
            if (v === 'virtual' || v === 'results_review_virtual') { setIncludePE(false); setPeUnremarkable(false); }
            else if (v === 'in_person_exam' || v === 'in_person_counseling') { setIncludePE(true); setPeUnremarkable(false); }
          }}>
            <SelectTrigger className="h-10 sm:h-9 text-sm bg-input border-border"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">&mdash;</SelectItem>
              {VISIT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Panel Ownership */}
        <div className="sm:w-28">
          <Label className="text-[10px] sm:text-xs text-muted-foreground">Panel</Label>
          <Select value={panelOwnership} onValueChange={setPanelOwnership}>
            <SelectTrigger className="h-10 sm:h-9 text-sm bg-input border-border"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PANEL_OWNERSHIP.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Appointment Type */}
        <div className="sm:w-28">
          <Label className="text-[10px] sm:text-xs text-muted-foreground">Appt</Label>
          <Select value={appointmentType || '_none'} onValueChange={(v) => setAppointmentType(v === '_none' ? '' : v)}>
            <SelectTrigger className="h-10 sm:h-9 text-sm bg-input border-border"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">&mdash;</SelectItem>
              {APPOINTMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {/* Status (Military/Civilian) */}
        <div className="col-span-2 sm:col-span-1 sm:w-28">
          <Label className={`text-[10px] sm:text-xs ${autoFilledFields.status ? 'text-emerald-400' : 'text-muted-foreground'}`}>Status</Label>
          <Select value={patientStatus || '_none'} onValueChange={(v) => { setPatientStatus(v === '_none' ? '' : v); setAutoFilledFields(prev => ({ ...prev, status: false })); }}>
            <SelectTrigger data-testid="patient-status-select" className={`h-10 sm:h-9 text-sm bg-input border-border ${autoFilledFields.status ? 'border-emerald-500/50' : ''}`}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">&mdash;</SelectItem>
              {PATIENT_STATUSES.map((status) => <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
};

export default ProviderDemographics;
